import { gql, useQuery } from '@apollo/client';
import NpvByProductionChartThumbnail from '@assets/images/report-components/npv-by-production-chart-thumbnail.png';
import { useEffect, useMemo, useState } from 'react';
import { GraphWrapper } from '@/admins/SmartReports/components/Components/Charts/GraphWrapper';
import { getLayout } from '@/admins/SmartReports/components/Components/Charts/helpers';
import { ComponentBox } from '@/admins/SmartReports/components/Components/Common/ComponentBox';
import { ComponentTitle } from '@/admins/SmartReports/components/Components/Common/ComponentTitle';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';
import { useChartColors } from '@/hooks/useChartColors';
import {
  AssetAggregateByEnum,
  type AssetAggregates,
  AssetAggregatesFieldsEnum,
} from '@/types/GraphqlSchema';
import { LoadingErrorWrapper } from '../Common/LoadingErrorWrapper';
import {
  type ComponentDefinition,
  type ComponentEditProps,
  ComponentViewMode,
  type ComponentViewProps,
  type UseDataType,
} from '../types';

const QUERY = gql`
  query AssetAggregates($input: AssetAggregatesInput!) {
    assetAggregates(input: $input) {
      type
      group
      totalProductionsSum
      totalReservesSum
      npv10Sum
    }
  }
`;

export const COMPONENT_ID = 'npv_by_production_chart';

const useData = ({ portfolio, persistedData, onComponentLoad }: UseDataType<AssetAggregates[]>) => {
  const [chartDataFetched, setChartDataFetched] = useState(persistedData);

  const { data, loading, error } = useQuery<{ assetAggregates: AssetAggregates[] }>(QUERY, {
    variables: {
      input: {
        by: AssetAggregateByEnum.Asset,
        fields: [
          AssetAggregatesFieldsEnum.TotalProductionsSum,
          AssetAggregatesFieldsEnum.TotalReservesSum,
          AssetAggregatesFieldsEnum.Npv10Sum,
        ],
        portfolio,
      },
    },
    skip: !!persistedData,
  });

  useEffect(() => {
    if (data?.assetAggregates) {
      const chartData = data.assetAggregates;
      setChartDataFetched(chartData);
      if (onComponentLoad) {
        onComponentLoad({
          componentId: COMPONENT_ID,
          data: chartData,
        });
      }
    }
  }, [data, onComponentLoad]);

  return { chartData: chartDataFetched, error, loading };
};

const ComponentView = ({
  data,
  mode = ComponentViewMode.View,
}: ComponentViewProps<AssetAggregates[]>) => {
  const chartColors = useChartColors();
  const formattedData = useMemo(() => {
    try {
      if (!data || !data.length) return null;

      // Calculate marker sizes based on reserves, scaling them to a range of 1 to 100
      const reserves = data.map(agg => agg.totalReservesSum);
      const validReserves = reserves.filter((r): r is number => r !== null);
      const min = Math.min(...validReserves);
      const max = Math.max(...validReserves);
      const markerSizes = reserves.map(r =>
        (!r && r !== 0) || max === min ? 50 : ((r - min) / (max - min)) * 99 + 1,
      );

      const formattedChartData = [
        {
          x: data.map(agg => agg.npv10Sum),
          y: data.map(agg => agg.totalProductionsSum),
          type: 'scatter',
          mode: 'markers',
          marker: {
            size: markerSizes,
            color: data.map((_, i) => chartColors[i % chartColors.length]), // Assign each marker a different color
          },
          text: data.map(agg => agg.group),
          hovertemplate:
            'Asset: %{text}<br>NPV10: %{x}<br>Production: %{y:,.2f} boe/d<br>Reserves: %{marker.size:,.2f} MMboe',
        },
      ];

      return formattedChartData;
    } catch {
      return null;
    }
  }, [data, chartColors]);

  if (!formattedData) return null;

  return (
    <ComponentBox>
      <ComponentTitle>NPV10 By Production</ComponentTitle>
      <GraphWrapper
        data={formattedData}
        layout={getLayout({
          showlegend: false,
          yaxis: { title: { text: 'boe/d' } },
          xaxis: { title: { text: 'NPV10' }, type: 'linear' },
        })}
        config={{
          staticPlot: mode !== ComponentViewMode.View,
        }}
      />
    </ComponentBox>
  );
};

const ComponentEdit = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: ComponentEditProps<AssetAggregates[]>) => {
  const { chartData, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={chartData}>
      {chartData && <ComponentView data={chartData} mode={ComponentViewMode.Edit} />}
    </LoadingErrorWrapper>
  );
};

const ComponentThumbnail = () => (
  <ComponentBox>
    <ComponentTitle sx={{ fontSize: '0.25rem', marginBottom: 0 }}>NPV By Production</ComponentTitle>
    <img
      src={NpvByProductionChartThumbnail}
      alt='NPV By Production Chart Thumbnail'
      style={{ width: '100%', height: 'auto' }}
    />
  </ComponentBox>
);

export const NpvByProductionChartComponent: ComponentDefinition<AssetAggregates[]> = {
  id: COMPONENT_ID,
  name: 'NPV By Production Chart',
  type: SmartReportComponentTypesEnum.Chart,
  reportTypes: [SmartReportTypeEnum.Corporate],
  Thumbnail: ComponentThumbnail,
  Component: ComponentEdit,
  ComponentView,
};
